// Location Picker Component with Google Maps
// Feature: 005-business-administration

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { MapPin, Loader2 } from 'lucide-react';

interface LocationPickerProps {
  latitude: number | null;
  longitude: number | null;
  onChange: (lat: number, lng: number, address?: string) => void;
  disabled?: boolean;
}

// Default location: Phnom Penh, Cambodia
const DEFAULT_CENTER = { lat: 11.5564, lng: 104.9282 };
const DEFAULT_ZOOM = 12;

/**
 * Reverse geocode coordinates to get a formatted address
 * Uses Google Geocoding API with region bias for Cambodia
 * @param lang - Language code for results (en, km, fr)
 */
async function reverseGeocode(
  lat: number,
  lng: number,
  apiKey: string,
  lang: string = 'en'
): Promise<string | null> {
  try {
    // Use region=KH to bias results towards Cambodia
    // Use the user's language for result formatting
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}&language=${lang}&region=KH`;
    console.log('[LocationPicker] Geocoding request:', { lat, lng, lang });
    
    const response = await fetch(url);
    
    if (!response.ok) {
      console.error('[LocationPicker] Geocoding API HTTP error:', response.status);
      return null;
    }
    
    const data = await response.json();
    console.log('[LocationPicker] Geocoding response:', data.status, data.results?.length || 0, 'results');
    
    if (data.status !== 'OK' || !data.results?.length) {
      console.error('[LocationPicker] Geocoding failed:', data.status, data.error_message);
      return null;
    }
    
    // Try to find a meaningful address component
    // Priority: locality (city), administrative_area_level_1 (province), formatted_address
    const result = data.results[0];
    
    // Extract city and province/state for a clean location string
    let city = '';
    let province = '';
    let country = '';
    
    for (const component of result.address_components || []) {
      if (component.types.includes('locality')) {
        city = component.long_name;
      } else if (component.types.includes('administrative_area_level_1')) {
        province = component.long_name;
      } else if (component.types.includes('country')) {
        country = component.long_name;
      }
    }
    
    // Build a clean location string
    let address: string;
    if (city && province) {
      address = `${city}, ${province}`;
    } else if (city) {
      address = city;
    } else if (province) {
      address = province;
    } else if (country) {
      address = country;
    } else {
      // Fallback to formatted address (first part before the country code)
      const formatted = result.formatted_address || '';
      // Remove the country from the end if present
      const parts = formatted.split(',').slice(0, -1);
      address = parts.length > 0 ? parts.join(',').trim() : formatted;
    }
    
    console.log('[LocationPicker] Geocoded address:', address);
    return address;
  } catch (error) {
    console.error('[LocationPicker] Reverse geocoding error:', error);
    return null;
  }
}

export function LocationPicker({
  latitude,
  longitude,
  onChange,
  disabled = false,
}: LocationPickerProps) {
  const { t, i18n } = useTranslation('businessAdmin');
  
  // Get API key from environment
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  
  // Get current language (normalize to base code: en-GB -> en, km-KH -> km)
  const currentLang = i18n.language.split('-')[0] || 'en';
  
  // Current marker position
  const [markerPosition, setMarkerPosition] = useState<{ lat: number; lng: number } | null>(
    latitude && longitude ? { lat: latitude, lng: longitude } : null
  );
  
  // Map center (separate from marker to allow panning)
  const [mapCenter] = useState<{ lat: number; lng: number }>(
    latitude && longitude ? { lat: latitude, lng: longitude } : DEFAULT_CENTER
  );
  
  // Loading state for geocoding
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodedAddress, setGeocodedAddress] = useState<string | null>(null);

  // Handle map click to place/move marker
  const handleMapClick = useCallback(
    async (event: any) => {
      if (disabled || !event.detail?.latLng) return;

      const { lat, lng } = event.detail.latLng;

      setMarkerPosition({ lat, lng });
      setGeocodedAddress(null);
      
      // Perform reverse geocoding with user's current language
      if (apiKey) {
        setIsGeocoding(true);
        const address = await reverseGeocode(lat, lng, apiKey, currentLang);
        setIsGeocoding(false);
        setGeocodedAddress(address);
        onChange(lat, lng, address || undefined);
      } else {
        onChange(lat, lng);
      }
    },
    [disabled, onChange, apiKey, currentLang]
  );

  // If no API key, show error
  if (!apiKey) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <p className="font-semibold">Google Maps API Key Missing</p>
        <p className="text-sm mt-1">
          Please add VITE_GOOGLE_MAPS_API_KEY to your .env file
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Instructions */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
        <MapPin size={16} className="mt-0.5 flex-shrink-0" />
        <p>{t('edit.fields.mapHint')}</p>
      </div>

      {/* Map Container */}
      <div className="relative w-full h-96 rounded-lg overflow-hidden border-2 border-neutral-300">
        <APIProvider apiKey={apiKey}>
          <Map
            defaultCenter={mapCenter}
            defaultZoom={DEFAULT_ZOOM}
            onClick={handleMapClick}
            gestureHandling={disabled ? 'none' : 'greedy'}
            disableDefaultUI={false}
            mapId="business-location-picker"
          >
            {markerPosition && (
              <AdvancedMarker
                position={markerPosition}
                title="Business Location"
              />
            )}
          </Map>
        </APIProvider>

        {/* Disabled overlay */}
        {disabled && (
          <div className="absolute inset-0 bg-neutral-900/10 flex items-center justify-center">
            <div className="bg-white px-4 py-2 rounded-lg shadow-lg text-neutral-600">
              {t('edit.fields.mapDisabled')}
            </div>
          </div>
        )}
      </div>

      {/* Current Coordinates Display */}
      {markerPosition && (
        <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200 text-sm space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-primary-600" />
              <span className="font-medium text-neutral-700">{t('edit.fields.selectedLocation')}</span>
            </div>
            <div className="font-mono text-neutral-600">
              {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
            </div>
          </div>
          {/* Geocoded address */}
          {isGeocoding && (
            <div className="flex items-center gap-2 text-neutral-500">
              <Loader2 size={14} className="animate-spin" />
              <span>Looking up address...</span>
            </div>
          )}
          {geocodedAddress && !isGeocoding && (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 px-2 py-1 rounded">
              <MapPin size={14} />
              <span>Address: {geocodedAddress}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
